"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useUser } from "@clerk/nextjs";
import type {
  SavedListingsMap,
  SavedListingsRequest,
  SavedListingsResponse,
} from "@/lib/types/user-listings";

type Props = {
  listingIds: string[];
  children: React.ReactNode;
};

const SavedListingsContext = createContext<SavedListingsMap | null>(null);

export const useSavedListingsMap = () => {
  return useContext(SavedListingsContext);
};

const SavedListingsProvider: React.FC<Props> = ({ listingIds, children }) => {
  const { user } = useUser();
  const [savedMap, setSavedMap] = useState<SavedListingsMap>({});

  const sortedIds = useMemo(() => {
    if (!listingIds || listingIds.length === 0) return [] as string[];
    return Array.from(new Set(listingIds)).sort();
  }, [listingIds]);

  const idsKey = useMemo(() => {
    if (sortedIds.length === 0) return "";
    return sortedIds.join(",");
  }, [sortedIds]);

  useEffect(() => {
    if (!user || sortedIds.length === 0) {
      setSavedMap({});
      return;
    }

    let isActive = true;

    const loadSavedMap = async () => {
      try {
        const body: SavedListingsRequest = { listingIds: sortedIds };
        const response = await fetch("/api/user/listings/saved", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
          cache: "no-store",
        });

        const payload = (await response.json()) as SavedListingsResponse;

        if (!isActive) return;

        if (!response.ok || !payload.success) {
          setSavedMap({});
          return;
        }

        setSavedMap(payload.data || {});
      } catch (error) {
        if (isActive) {
          console.error("Failed to load saved listings", error);
          setSavedMap({});
        }
      }
    };

    loadSavedMap();

    return () => {
      isActive = false;
    };
  }, [idsKey, user?.id, sortedIds]);

  return (
    <SavedListingsContext.Provider value={savedMap}>
      {children}
    </SavedListingsContext.Provider>
  );
};

export default SavedListingsProvider;
